import "./styles.css";
import { MONSTERS, OBJECT_TYPES, LEVELS } from "./game/data/gameData.js";
import { MonsterGame } from "./game/MonsterGame.js";
import { SynthAudio } from "./game/systems/sound.js";

const STORAGE_KEY = "monster-game-progress-v1";

const audio = new SynthAudio();
const state = {
  selectedMonsterId: MONSTERS[0].id,
  selectedLevelId: LEVELS[0].id,
  progress: loadProgress(),
  gameActive: false,
};

// ── DOM refs ────────────────────────────────────────────────────────────────
const $ = (id) => document.getElementById(id);

const el = {
  lobby:         $("lobby"),
  ingameHud:     $("ingame-hud"),
  launchButton:  $("launch-button"),
  restartButton: $("restart-button"),
  toLobbyButton: $("to-lobby-button"),
  statusBanner:  $("status-banner"),
  resultOverlay: $("result-overlay"),
  resultKicker:  $("result-kicker"),
  resultTitle:   $("result-title"),
  resultBody:    $("result-body"),
  monsterPicker: $("monster-picker"),
  monsterDetail: $("monster-detail"),
  levelPicker:   $("level-picker"),
  levelDetail:   $("level-detail"),
  legendGrid:    $("legend-grid"),
  helpBtn:       $("help-btn"),
  helpModal:     $("help-modal"),
  helpClose:     $("help-close"),
  menuBtn:       $("menu-btn"),
  menuModal:     $("menu-modal"),
  menuResume:    $("menu-resume"),
  menuQuit:      $("menu-quit"),
  hudTime:       $("hud-time"),
  hudTower:      $("hud-tower"),
  hudTowerBar:   $("hud-tower-bar"),
  hudStage:      $("hud-stage"),
  hudConsumed:   $("hud-consumed"),
  hudAbility:    $("hud-ability"),
  hudDash:       $("hud-dash"),
};

// ── MonsterGame ──────────────────────────────────────────────────────────────
const monsterGame = new MonsterGame({
  parentId: "phaser-root",
  audio,
  onHudChange: updateHud,
  onStatus:    setStatus,
  onResult:    handleResult,
});

// ── Mobile detection & joystick ─────────────────────────────────────────────
// Touch device OR portrait orientation OR small screen
const isMobile = ('ontouchstart' in window || navigator.maxTouchPoints > 0)
  || window.innerWidth < window.innerHeight
  || window.innerWidth <= 768;

let joystickInstance = null;
let joystickReady = Promise.resolve();

if (isMobile) {
  $("touch-buttons").classList.remove("hidden");
  const zone = $("joystick-zone");
  zone.style.display = 'block';
  joystickReady = import('./game/systems/joystick.js').then(({ createMobileJoystick }) => {
    joystickInstance = createMobileJoystick(zone);
    monsterGame.setJoystickState(joystickInstance.state);
  });
}

$("touch-ability")?.addEventListener("touchstart", (e) => {
  e.preventDefault();
  monsterGame.triggerAbility();
}, { passive: false });

$("touch-dash")?.addEventListener("touchstart", (e) => {
  e.preventDefault();
  monsterGame.triggerDash();
}, { passive: false });

// ── Black-key transparency for HTML icon images ───────────────────────────
function applyBlackKeyToImg(imgEl, threshold = 32) {
  const canvas = document.createElement('canvas');
  canvas.width = imgEl.naturalWidth || imgEl.width || 64;
  canvas.height = imgEl.naturalHeight || imgEl.height || 64;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(imgEl, 0, 0);
  const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
  const data = imageData.data;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    if (r < threshold && g < threshold && b < threshold) {
      data[i + 3] = 0;
    }
  }
  ctx.putImageData(imageData, 0, 0);
  imgEl.src = canvas.toDataURL('image/png');
}

function processIconImages() {
  document.querySelectorAll('.touch-btn-icon').forEach((img) => {
    if (img.complete && img.naturalWidth > 0) {
      applyBlackKeyToImg(img);
    } else {
      img.addEventListener('load', () => applyBlackKeyToImg(img), { once: true });
    }
  });
}
processIconImages();

// ── Show game-wrap, hide loading screen (initial JS bootstrap done) ───────
const gameWrapEl = document.getElementById('game-wrap');
const loadingScreenEl = document.getElementById('loading-screen');
if (gameWrapEl) gameWrapEl.style.visibility = 'visible';
if (loadingScreenEl) loadingScreenEl.classList.add('hidden');

// ── Initial render ───────────────────────────────────────────────────────────

renderMonsterPicker();
renderLevelPicker();
renderLegend();

// ── Lobby launch ─────────────────────────────────────────────────────────────
el.launchButton.addEventListener("click", async () => {
  // Show loading screen while Phaser loads assets
  const ls = document.getElementById('loading-screen');
  if (ls) {
    ls.style.opacity = '1';
    ls.style.transition = '';
    ls.classList.remove('hidden');
    const bar = document.getElementById('loading-progress-bar');
    const txt = document.getElementById('loading-progress-text');
    if (bar) bar.style.width = '0%';
    if (txt) txt.textContent = '0%';
  }
  showIngame();
  setStatus("Город ещё не знает, что произойдёт…");
  await joystickReady;
  const lvl = LEVELS.find((l) => l.id === state.selectedLevelId) ?? LEVELS[0];
  await monsterGame.start(state.selectedMonsterId, lvl);
});

// ── Result buttons ────────────────────────────────────────────────────────────
el.restartButton.addEventListener("click", async () => {
  el.resultOverlay.classList.add("hidden");
  showIngame();
  setStatus("Новый заход. Город снова на тарелке.");
  await monsterGame.restart();
});

el.toLobbyButton.addEventListener("click", () => {
  el.resultOverlay.classList.add("hidden");
  monsterGame.stop();
  showLobby();
});

// ── Help modal ────────────────────────────────────────────────────────────────
el.helpBtn.addEventListener("click", () => {
  el.helpModal.classList.remove("hidden");
});
el.helpClose.addEventListener("click", () => {
  el.helpModal.classList.add("hidden");
});
el.helpModal.addEventListener("click", (e) => {
  if (e.target === el.helpModal) el.helpModal.classList.add("hidden");
});

// ── Menu / pause modal ────────────────────────────────────────────────────────
el.menuBtn.addEventListener("click", () => {
  el.menuModal.classList.remove("hidden");
});
el.menuResume.addEventListener("click", () => {
  el.menuModal.classList.add("hidden");
});
el.menuQuit.addEventListener("click", () => {
  el.menuModal.classList.add("hidden");
  monsterGame.stop();
  showLobby();
});
el.menuModal.addEventListener("click", (e) => {
  if (e.target === el.menuModal) el.menuModal.classList.add("hidden");
});

// ── State helpers ─────────────────────────────────────────────────────────────
function showLobby() {
  state.gameActive = false;
  el.lobby.classList.remove("hidden");
  el.ingameHud.classList.add("hidden");
  renderMonsterPicker();
  renderLevelPicker();
}

function showIngame() {
  state.gameActive = true;
  el.lobby.classList.add("hidden");
  el.ingameHud.classList.remove("hidden");
  el.resultOverlay.classList.add("hidden");
}

// ── Monster picker render ──────────────────────────────────────────────────────
function renderMonsterPicker() {
  el.monsterPicker.innerHTML = "";

  for (const monster of MONSTERS) {
    const bestStars = state.progress[monster.id]?.bestStars;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `monster-pick-btn${monster.id === state.selectedMonsterId ? " is-selected" : ""}`;
    btn.innerHTML = `
      <img class="pick-icon" src="/assets/${monster.sprite}.png" alt="${monster.name}">
      <span class="pick-name">${monster.name}</span>
      <span class="pick-tag">${bestStars ? "★".repeat(bestStars) : monster.cardTag}</span>
    `;
    btn.addEventListener("click", () => {
      state.selectedMonsterId = monster.id;
      renderMonsterPicker();
    });
    el.monsterPicker.append(btn);
  }

  renderMonsterDetail();
}

function renderMonsterDetail() {
  const monster = MONSTERS.find((m) => m.id === state.selectedMonsterId) ?? MONSTERS[0];
  el.monsterDetail.innerHTML = `
    <strong>${monster.name} — ${monster.title}</strong>
    ${monster.description}<br>
    <em style="color:var(--accent-2);font-size:0.82rem">${monster.ability.hint}</em>
  `;
}

// ── Level picker render ───────────────────────────────────────────────────────
function renderLevelPicker() {
  if (!el.levelPicker) return;
  el.levelPicker.innerHTML = "";
  for (const lvl of LEVELS) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = `level-pick-btn${lvl.id === state.selectedLevelId ? " is-selected" : ""}`;
    btn.innerHTML = `<span class="lvl-name">${lvl.name}</span><span class="lvl-sub">${lvl.subtitle}</span>`;
    btn.addEventListener("click", () => {
      state.selectedLevelId = lvl.id;
      renderLevelPicker();
    });
    el.levelPicker.append(btn);
  }
  renderLevelDetail();
}

function renderLevelDetail() {
  if (!el.levelDetail) return;
  const lvl = LEVELS.find((l) => l.id === state.selectedLevelId) ?? LEVELS[0];
  el.levelDetail.textContent = `${lvl.subtitle}`;
}

// ── Legend render ─────────────────────────────────────────────────────────────
function renderLegend() {
  const featured = [
    OBJECT_TYPES[0],
    OBJECT_TYPES[4],
    OBJECT_TYPES[6],
    OBJECT_TYPES[8],
    OBJECT_TYPES[10],
  ];

  el.legendGrid.innerHTML = "";
  for (const entry of featured) {
    const card = document.createElement("div");
    card.className = "legend-item";
    card.innerHTML = `
      <div>
        <h3>${entry.label}</h3>
        <p>Размер ${entry.requiredSize.toFixed(1)}</p>
      </div>
      <span class="legend-swatch" style="background:${toHex(entry.color)}"></span>
    `;
    el.legendGrid.append(card);
  }
}

// ── HUD update ────────────────────────────────────────────────────────────────
function updateHud(nextHud) {
  el.hudTime.textContent       = nextHud.time;
  el.hudTower.textContent      = nextHud.tower;
  el.hudTowerBar.style.width   = `${nextHud.towerPercent}%`;
  el.hudStage.textContent      = nextHud.stage;
  el.hudConsumed.textContent   = `${nextHud.consumed} съедено`;
  el.hudAbility.textContent    = `E: ${nextHud.ability}`;
  el.hudDash.textContent       = `⚡ ${nextHud.dash}`;
}

// ── Status ────────────────────────────────────────────────────────────────────
function setStatus(text) {
  el.statusBanner.textContent = text;
}

// ── Result ────────────────────────────────────────────────────────────────────
function handleResult(result) {
  const stars    = result.stars ?? 0;
  const starFull  = "★";
  const starEmpty = "☆";
  const starStr   = starFull.repeat(stars) + starEmpty.repeat(3 - stars);

  el.resultKicker.textContent = result.won ? "Победа!" : "Время вышло";
  el.resultTitle.textContent  = result.won
    ? `${result.monsterName} уничтожил башню!`
    : "Город устоял";
  el.resultBody.innerHTML = `
    <div class="star-rating" aria-label="${stars} из 3 звёзд">${starStr}</div>
    <p>Съедено: ${result.consumed} · Размер: ${result.size.toFixed(1)} · Время: ${formatTime(result.timeSpent)}</p>
  `;

  const prev = state.progress[result.monsterId] ?? {};
  state.progress[result.monsterId] = {
    ...prev,
    bestStars: Math.max(prev.bestStars ?? 0, stars),
    bestTime: result.won
      ? Math.min(prev.bestTime ?? Infinity, result.timeSpent)
      : prev.bestTime,
  };
  saveProgress(state.progress);

  el.ingameHud.classList.add("hidden");
  el.resultOverlay.classList.remove("hidden");
  setStatus(result.won ? "Забег завершён победой." : "Забег завершён поражением.");
}

// ── Helpers ───────────────────────────────────────────────────────────────────
function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveProgress(progress) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
}

function formatTime(totalSeconds) {
  const safe = Math.max(0, Math.round(totalSeconds));
  const minutes = String(Math.floor(safe / 60)).padStart(2, "0");
  const seconds = String(safe % 60).padStart(2, "0");
  return `${minutes}:${seconds}`;
}

function toHex(color) {
  return `#${color.toString(16).padStart(6, "0")}`;
}
